/**
 * SchemaListPanel
 *
 * Panel for listing, creating, and managing data schemas.
 * Can be used in workflow editor sidebar.
 */

import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Plus,
  MoreVertical,
  Pencil,
  Copy,
  Trash2,
  Database,
  Loader2,
  Search,
  RefreshCw,
  CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useSchemaStore, SchemaDefinition } from '@/stores/schemas/schema-store';

interface SchemaListPanelProps {
  className?: string;
  onSchemaSelect?: (schema: SchemaDefinition) => void;
}

export const SchemaListPanel: React.FC<SchemaListPanelProps> = ({
  className,
  onSchemaSelect,
}) => {
  const {
    schemas,
    isLoading,
    error,
    fetchSchemas,
    deleteSchema,
    duplicateSchema,
    publishSchema,
    openBuilder,
  } = useSchemaStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [schemaToDelete, setSchemaToDelete] = useState<SchemaDefinition | null>(null);

  // Fetch schemas on mount
  useEffect(() => {
    fetchSchemas();
  }, [fetchSchemas]);

  // Filter schemas by search
  const filteredSchemas = schemas.filter((schema) =>
    schema.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    schema.displayName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    schema.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Handle create new schema
  const handleCreateNew = () => {
    openBuilder();
  };

  // Handle edit schema
  const handleEdit = (schema: SchemaDefinition) => {
    openBuilder(schema);
  };

  // Handle duplicate
  const handleDuplicate = async (schema: SchemaDefinition) => {
    const duplicated = await duplicateSchema(schema.id!);
    if (duplicated) {
      toast.success('Schema duplicated', { description: duplicated.name });
    } else {
      toast.error('Failed to duplicate schema');
    }
  };

  // Handle publish
  const handlePublish = async (schema: SchemaDefinition) => {
    const published = await publishSchema(schema.id!);
    if (published) {
      toast.success('Schema published', { description: published.name });
    } else {
      toast.error('Failed to publish schema');
    }
  };

  // Handle delete confirmation
  const handleDeleteClick = (schema: SchemaDefinition) => {
    setSchemaToDelete(schema);
    setDeleteDialogOpen(true);
  };

  // Handle delete confirmed
  const handleDeleteConfirm = async () => {
    if (!schemaToDelete) return;

    const success = await deleteSchema(schemaToDelete.id!);
    if (success) {
      toast.success('Schema deleted');
    } else {
      toast.error('Failed to delete schema');
    }

    setDeleteDialogOpen(false);
    setSchemaToDelete(null);
  };

  // Handle schema click
  const handleSchemaClick = (schema: SchemaDefinition) => {
    if (onSchemaSelect) {
      onSchemaSelect(schema);
    } else {
      handleEdit(schema);
    }
  };

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Header */}
      <div className="p-3 border-b flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <Database className="w-4 h-4" />
          Schemas
        </h3>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={() => fetchSchemas()}
            disabled={isLoading}
          >
            <RefreshCw className={cn('w-4 h-4', isLoading && 'animate-spin')} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleCreateNew}
          >
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="p-2 border-b">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search schemas..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 pl-8 text-sm"
          />
        </div>
      </div>

      {/* Schema list */}
      <ScrollArea className="flex-1">
        {isLoading && schemas.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="p-4 text-center text-sm text-destructive">{error}</div>
        ) : filteredSchemas.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            {searchQuery ? 'No schemas match your search' : 'No schemas yet'}
          </div>
        ) : (
          <div className="p-2 space-y-1">
            {filteredSchemas.map((schema) => (
              <SchemaListItem
                key={schema.id}
                schema={schema}
                onClick={() => handleSchemaClick(schema)}
                onEdit={() => handleEdit(schema)}
                onDuplicate={() => handleDuplicate(schema)}
                onPublish={() => handlePublish(schema)}
                onDelete={() => handleDeleteClick(schema)}
              />
            ))}
          </div>
        )}
      </ScrollArea>

      {/* Quick add button at bottom */}
      <div className="p-2 border-t">
        <Button
          variant="outline"
          size="sm"
          className="w-full"
          onClick={handleCreateNew}
        >
          <Plus className="w-4 h-4 mr-2" />
          New Schema
        </Button>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Schema</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{schemaToDelete?.name}"? This action cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Individual schema list item
interface SchemaListItemProps {
  schema: SchemaDefinition;
  onClick: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onPublish: () => void;
  onDelete: () => void;
}

const SchemaListItem: React.FC<SchemaListItemProps> = ({
  schema,
  onClick,
  onEdit,
  onDuplicate,
  onPublish,
  onDelete,
}) => {
  const fieldCount = schema.fields?.length || 0;
  const status = schema.status || 'DRAFT';

  return (
    <div
      className="group flex items-center gap-2 p-2 rounded-md hover:bg-accent cursor-pointer"
      onClick={onClick}
    >
      {/* Icon */}
      <div className="w-10 h-8 rounded border flex-shrink-0 bg-muted flex items-center justify-center">
        <Database className="w-4 h-4 text-muted-foreground" />
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">
          {schema.displayName || schema.name}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{fieldCount} field{fieldCount !== 1 ? 's' : ''}</span>
          <span>•</span>
          <Badge
            variant={status === 'PUBLISHED' ? 'default' : 'secondary'}
            className={cn(
              'h-4 px-1 text-[10px]',
              status === 'PUBLISHED' && 'bg-green-500 hover:bg-green-600'
            )}
          >
            {status}
          </Badge>
        </div>
      </div>

      {/* Actions */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity"
          >
            <MoreVertical className="w-4 h-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(); }}>
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </DropdownMenuItem>
          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onDuplicate(); }}>
            <Copy className="w-4 h-4 mr-2" />
            Duplicate
          </DropdownMenuItem>
          {status !== 'PUBLISHED' && (
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onPublish(); }}>
              <CheckCircle className="w-4 h-4 mr-2" />
              Publish
            </DropdownMenuItem>
          )}
          <Separator className="my-1" />
          <DropdownMenuItem
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-destructive focus:text-destructive"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default SchemaListPanel;
